import { useQuery } from 'react-apollo';

import { useSessionData } from '@confluence/session-data';
import { ConfluenceEdition } from '@confluence/change-edition';
import {
	confluenceLocalStorageInstance as localStorage,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';
import { useSpaceKey } from '@confluence/space-utils';
import { isGraphQLError } from '@confluence/network';
import { isErrorMarkedAsHandled, markErrorAsHandled } from '@confluence/graphql';
import { expValEquals } from '@confluence/feature-experiments';

import { useReverseTrialEligible } from '../useReverseTrialEligible';
import { TrialDetailsQuery } from '../TrialDetailsQuery.graphql';
import type { TrialDetailsQuery as TrialDetailsQueryType } from '../__types__/TrialDetailsQuery';
import { ReverseTrialCohort } from '../__types__/InstanceIsInReverseTrialQuery';
import { getTrialDaysLeft } from '../getTrialDaysLeft';

import { CalendarsDetailsQuery } from './CalendarsDetailsQuery.experimentalgraphql';

const SPACE_CALENDARS = 'spaceCalendars';
export enum Cohort {
	NOT_ENROLLED = 'not-enrolled',
	CONTROL = 'control',
	TEST = 'test',
}

export const useIsEligibleForDatesOnPage = () => {
	const { edition, isLoggedIn } = useSessionData();
	const spaceKey = useSpaceKey();

	const hasSeenModal = localStorage.getItemAsBoolean(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_TEAM_CALENDAR_DATES_ON_PAGE_HAS_SEEN,
	);

	const isUserEligible =
		!Boolean(process.env.REACT_SSR) &&
		edition === ConfluenceEdition.PREMIUM &&
		isLoggedIn &&
		!hasSeenModal &&
		!!spaceKey;

	//check if instance in trial
	const {
		data: trialData,
		loading: trialDataLoading,
		error: trialDetailsError,
	} = useQuery<TrialDetailsQueryType>(TrialDetailsQuery, {
		fetchPolicy: 'cache-first',
		skip: !isUserEligible,
	});

	//verify not in RT
	const { error: reverseTrialError, reverseTrialCohort } = useReverseTrialEligible({
		skip: !isUserEligible || trialDataLoading || !!trialDetailsError,
	});
	const isReverseTrialEnrolled = reverseTrialCohort === ReverseTrialCohort.ENROLLED;

	const isEligible =
		isUserEligible &&
		getTrialDaysLeft(trialData?.license?.trialEndDate) > 0 &&
		!isReverseTrialEnrolled &&
		expValEquals('team_calendar_dates_on_page', 'cohort', 'test');

	//verify if the user has created calendar before
	const { data: calendarsData, error: calendarsError } = useQuery(CalendarsDetailsQuery, {
		variables: {
			calendarContext: SPACE_CALENDARS,
			viewingSpaceKey: spaceKey,
		},
		fetchPolicy: 'cache-first',
		skip: !isEligible,
	});

	const error = trialDetailsError || reverseTrialError || calendarsError;
	if (isGraphQLError(error) && !isErrorMarkedAsHandled(error)) {
		markErrorAsHandled(error);
	}

	return {
		isEligible: calendarsError
			? isEligible
			: isEligible &&
				(calendarsData?.experimentalTeamCalendars?.payload === null ||
					calendarsData?.experimentalTeamCalendars?.payload?.length === 0),
		userTimeZoneId:
			calendarsData?.experimentalCurrentUser?.timeZone ||
			calendarsData?.experimentalSystemInfoSettings?.defaultTimeZone,
	};
};
